/**
 * Feature Flag Helper Types and Utilities
 *
 * This file contains derived types and utility functions for working with feature flags.
 * The actual flag definitions are in feature-flag-definitions.ts
 */

import { FEATURE_FLAGS } from './feature-flag-definitions';
import { FeatureFlagCategory, ResponsibleTeam } from './feature-flag.types';

/**
 * Extract all feature flag keys from the FEATURE_FLAGS object
 * This utility type recursively extracts the 'key' property from nested objects
 */
type ExtractFeatureFlagKeys<T> = T extends { key: string }
    ? T['key']
    : T extends Record<string, any>
      ? { [K in keyof T]: ExtractFeatureFlagKeys<T[K]> }[keyof T]
      : never;

/**
 * Type representing all valid feature flag keys
 * Automatically derived from FEATURE_FLAGS object
 * This creates a union type of all possible flag keys for compile-time safety
 */
export type FeatureFlagKey = ExtractFeatureFlagKeys<typeof FEATURE_FLAGS>;

/**
 * Type-safe FLAGS object for template usage
 * Provides compile-time type checking when using feature flags in templates
 * Usage: FLAGS.showAuditLogs instead of 'showAuditLogs' string literal
 */
export const FLAGS = Object.keys(FEATURE_FLAGS).reduce(
    (acc, key) => {
        acc[key as FeatureFlagKey] = key as FeatureFlagKey;
        return acc;
    },
    {} as Record<FeatureFlagKey, FeatureFlagKey>
);

/**
 * Array of all feature flag keys
 * Useful for iteration and bulk operations
 */
export const ALL_FEATURE_FLAG_KEYS = Object.keys(FEATURE_FLAGS) as FeatureFlagKey[];

/**
 * Array of development-specific feature flag keys
 * Filters flags that are marked as developmentOnly and in DEBUGGING category
 */
export const DEVELOPMENT_FEATURE_FLAGS = Object.entries(FEATURE_FLAGS)
    .filter(([_, flag]) => flag.developmentOnly && flag.category === FeatureFlagCategory.DEBUGGING)
    .map(([key]) => key as FeatureFlagKey);

/**
 * Helper type to extract feature flag keys by category
 * Usage: FeatureFlagKeysByCategory<FeatureFlagCategory.UI_FEATURES>
 */
type FilterFlagsByCategory<C extends FeatureFlagCategory> = {
    [K in FeatureFlagKey]: K extends keyof typeof FEATURE_FLAGS
        ? (typeof FEATURE_FLAGS)[K]['category'] extends C
            ? K
            : never
        : never;
};

export type FeatureFlagKeysByCategory<C extends FeatureFlagCategory> = FilterFlagsByCategory<C>[FeatureFlagKey];

/**
 * Helper type to extract feature flag keys by development status
 * Usage: FeatureFlagKeysByDevelopmentStatus<true> for dev-only flags
 */
type FilterFlagsByDevelopmentStatus<DevOnly extends boolean> = {
    [K in FeatureFlagKey]: K extends keyof typeof FEATURE_FLAGS
        ? (typeof FEATURE_FLAGS)[K]['developmentOnly'] extends DevOnly
            ? K
            : never
        : never;
};

export type FeatureFlagKeysByDevelopmentStatus<DevOnly extends boolean> =
    FilterFlagsByDevelopmentStatus<DevOnly>[FeatureFlagKey];

/**
 * Helper type to extract feature flag keys by responsible team
 * Usage: FeatureFlagKeysByTeam<'sit'>
 */
type FilterFlagsByTeam<Team extends ResponsibleTeam> = {
    [K in FeatureFlagKey]: K extends keyof typeof FEATURE_FLAGS
        ? (typeof FEATURE_FLAGS)[K]['responsibleTeam'] extends Team
            ? K
            : never
        : never;
};

export type FeatureFlagKeysByTeam<Team extends ResponsibleTeam> = FilterFlagsByTeam<Team>[FeatureFlagKey];

// Specific category type aliases for convenience
export type UIFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.UI_FEATURES>;
export type BetaFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.BETA>;
export type ExperimentalFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.EXPERIMENTAL>;
export type DebuggingFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.DEBUGGING>;
export type MaintenanceFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.MAINTENANCE>;
export type SecurityFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.SECURITY>;
export type AdminFeatureFlagKey = FeatureFlagKeysByCategory<FeatureFlagCategory.ADMIN>;

// Development status type aliases
export type DevelopmentOnlyFlagKey = FeatureFlagKeysByDevelopmentStatus<true>;
export type ProductionFlagKey = FeatureFlagKeysByDevelopmentStatus<false>;

// Team-specific type aliases
export type SitTeamFlagKey = FeatureFlagKeysByTeam<'sit'>;
export type DepotTeamFlagKey = FeatureFlagKeysByTeam<'depot'>;
export type IntegrationsTeamFlagKey = FeatureFlagKeysByTeam<'integrations'>;
export type BetaTeamFlagKey = FeatureFlagKeysByTeam<'beta'>;
